/**
 * 318 (FED-55): the Gallery map — where each photo was taken, for Gallery
 * document users and admins (photo.room; David, 29 Oct).
 *
 * The data is ONE read, photo_room_map(), which applies the same filters as
 * the grid (app.photo_room_matches on the server — one home, rule 11), so the
 * pins and the photos never disagree. Choosing a pin or a cluster hands its
 * paths back to the grid as p_paths: the database filters, never this file
 * (rule 1). The read records nothing (rule 8).
 *
 * This module is pure apart from loadGalleryMap, so the suite can import it.
 * The basemap key lives in lib/mapKey.ts for the same reason: import.meta.env
 * is not defined in the test bundle.
 */
import { supabase } from './supabase'
import { placeLine } from './photoRoom'
import { readFunctionError } from './roleSuggestions'

export type MapPoint = {
  path: string
  lat: number
  lng: number
  locality: string | null
  region: string | null
  country: string | null
}

export type GalleryMapData = {
  /** Photos the filters chose. */
  matched: number
  /** Of those, how many have a location. */
  located: number
  /** The most points the server sends; located > cap means the newest cap. */
  cap: number
  points: MapPoint[]
}

export type MapQuery = {
  query?: string | null
  values?: string[]
  from?: string | null
  to?: string | null
  tz?: string | null
  /** A search's whole set (it is held in the browser), or null for the filters. */
  paths?: string[] | null
}

/** What a pin or a cluster stands for, once chosen. */
export type MapPick = { key: string; paths: string[]; place: string | null }

/** The server refuses more than this many paths in one call (318). */
export const MAP_PATHS_MAX = 2000

const num = (v: unknown): number => {
  const n = typeof v === 'number' ? v : Number(v)
  return Number.isFinite(n) ? n : 0
}
const str = (v: unknown): string | null => (typeof v === 'string' && v.trim() ? v : null)

/** The server's jsonb as a GalleryMapData. A point without both coordinates is dropped. */
export function toGalleryMap(raw: unknown): GalleryMapData {
  const r = (raw ?? {}) as Record<string, unknown>
  const pts = Array.isArray(r.points) ? (r.points as Record<string, unknown>[]) : []
  const points: MapPoint[] = []
  for (const p of pts) {
    // Number(null) is 0, a real place off Africa: a missing value must stay missing.
    const lat = p.lat == null || p.lat === '' ? NaN : Number(p.lat)
    const lng = p.lng == null || p.lng === '' ? NaN : Number(p.lng)
    if (typeof p.path !== 'string' || !Number.isFinite(lat) || !Number.isFinite(lng)) continue
    points.push({ path: p.path, lat, lng, locality: str(p.locality), region: str(p.region), country: str(p.country) })
  }
  return { matched: num(r.matched), located: num(r.located), cap: num(r.cap) || MAP_PATHS_MAX, points }
}

/** "N without location": the photos the filters chose that have no point. */
export function withoutLocation(d: Pick<GalleryMapData, 'matched' | 'located'>): number {
  return Math.max(0, d.matched - d.located)
}

/** True when the map is showing only the newest `cap` of the located photos. */
export function isCapped(d: Pick<GalleryMapData, 'located' | 'cap' | 'points'>): boolean {
  return d.located > d.points.length
}

/** A point's place in plain words — the same words as the viewer's (placeLine). */
export function pointPlace(p: Pick<MapPoint, 'locality' | 'region' | 'country'>): string | null {
  return placeLine(p)
}

/** The place every one of these points shares - the full place, else the
 *  country - or null. Never the commonest: a group of 14 photos in Antofagasta
 *  and 6 in Santiago is not "near Antofagasta". */
export function commonPlace(points: Pick<MapPoint, 'locality' | 'region' | 'country'>[]): string | null {
  if (points.length === 0) return null
  const same = (keys: (string | null)[]): string | null =>
    keys.every((k) => k !== null && k === keys[0]) ? keys[0] : null
  return same(points.map(pointPlace)) ?? same(points.map((p) => p.country))
}

/** The points as GeoJSON for a clustered source. */
export function toFeatureCollection(points: MapPoint[]) {
  return {
    type: 'FeatureCollection' as const,
    features: points.map((p) => ({
      type: 'Feature' as const,
      properties: { path: p.path, place: pointPlace(p) ?? '' },
      geometry: { type: 'Point' as const, coordinates: [p.lng, p.lat] },
    })),
  }
}

/** The bounds of the points as [[west, south], [east, north]], or null. */
export function boundsOf(points: Pick<MapPoint, 'lat' | 'lng'>[]): [[number, number], [number, number]] | null {
  if (points.length === 0) return null
  let w = 180, s = 90, e = -180, n = -90
  for (const p of points) {
    w = Math.min(w, p.lng); e = Math.max(e, p.lng)
    s = Math.min(s, p.lat); n = Math.max(n, p.lat)
  }
  return [[w, s], [e, n]]
}

export type MapColours = { sea: string; land: string; border: string }

/**
 * The basemaps a viewer can choose between, with a MapTiler key (David, 29 Oct):
 * streets; satellite with labels (MapTiler's "hybrid"); and topo - contours,
 * hillshade and a clear road hierarchy, which read better for finding a site
 * than "outdoor" (trails, transit, blue streams close to our blue pins).
 * Every one of them fetches from api.maptiler.com only (style, tiles, sprites,
 * fonts, terrain), so the CSP needs no new origin. Without a key there is no
 * choice: the outline map is the only basemap.
 */
export type Basemap = 'streets' | 'satellite' | 'topo'
export const BASEMAPS: readonly Basemap[] = ['streets', 'satellite', 'topo']
const MAPTILER_STYLE: Record<Basemap, string> = { streets: 'streets-v2', satellite: 'hybrid', topo: 'topo-v2' }

/** The MapTiler style URL for one basemap (the key URL-encoded). */
export function basemapUrl(key: string, basemap: Basemap): string {
  return `https://api.maptiler.com/maps/${MAPTILER_STYLE[basemap]}/style.json?key=${encodeURIComponent(key)}`
}

/**
 * The basemap. With a MapTiler key (David, 29 Oct), the chosen MapTiler style
 * (streets unless said otherwise); the key is a public, origin-restricted
 * client key. Without one, an outline map drawn from Natural Earth (public
 * domain) shipped with the app, so no vendor is asked anything — and the panel
 * says it is an outline.
 */
export function mapStyle(key: string | null, c: MapColours, basemap: Basemap = 'streets'): string | Record<string, unknown> {
  if (key) return basemapUrl(key, basemap)
  return {
    version: 8,
    sources: {
      world: { type: 'geojson', data: '/map/countries-110m.json', attribution: 'Natural Earth' },
    },
    layers: [
      { id: 'sea', type: 'background', paint: { 'background-color': c.sea } },
      { id: 'land', type: 'fill', source: 'world', paint: { 'fill-color': c.land } },
      { id: 'borders', type: 'line', source: 'world', paint: { 'line-color': c.border, 'line-width': 0.6 } },
    ],
  }
}

/** Which basemap this browser last chose, for both maps: a per-viewer
 *  convenience, never stored on the server. Anything unknown reads as streets. */
const BASEMAP_PREF = 'oneconetec.map.basemap'
export function readBasemap(): Basemap {
  try {
    const v = window.localStorage.getItem(BASEMAP_PREF)
    return (BASEMAPS as readonly string[]).includes(v ?? '') ? (v as Basemap) : 'streets'
  } catch {
    return 'streets'
  }
}
export function writeBasemap(b: Basemap): void {
  try {
    window.localStorage.setItem(BASEMAP_PREF, b)
  } catch {
    /* private window: the map just starts on streets next time */
  }
}

/** The origin a keyed basemap fetches from — the CSP allows it only when a key is set. */
export const MAPTILER_ORIGIN = 'https://api.maptiler.com'

/** Whether the map is open: a per-viewer convenience in this browser only. */
const PREF = 'oneconetec.gallery.map'
export function readMapPref(): boolean {
  try {
    return window.localStorage.getItem(PREF) === 'on'
  } catch {
    return false
  }
}
export function writeMapPref(on: boolean): void {
  try {
    window.localStorage.setItem(PREF, on ? 'on' : 'off')
  } catch {
    /* private window: the map just starts closed next time */
  }
}

export async function loadGalleryMap(q: MapQuery): Promise<GalleryMapData> {
  const { data, error } = await supabase.rpc('photo_room_map', {
    p_query: q.query && q.query.trim() ? q.query : null,
    p_values: q.values && q.values.length ? q.values : null,
    p_from: q.from ?? null,
    p_to: q.to ?? null,
    p_tz: q.tz ?? null,
    p_paths: q.paths ?? null,
  })
  if (error) throw error
  return toGalleryMap(data)
}

/* ── 319: add or move a photo's location ───────────────────────────────────── */

/** What the caller may do with one photo's location (photo_place_state). */
export type PlaceState = {
  path: string
  hasLocation: boolean
  lat: number | null
  lng: number | null
  source: 'camera' | 'picked' | null
  locatedByName: string | null
  /** The poster said no to keeping where it was taken. */
  optedOut: boolean
  isPoster: boolean
  /** place_photo would accept this caller for this photo. */
  mayPlace: boolean
}

export function toPlaceState(r: Record<string, unknown>): PlaceState {
  const n = (v: unknown) => (v === null || v === undefined || v === '' ? null : Number.isFinite(Number(v)) ? Number(v) : null)
  return {
    path: String(r.storage_path),
    hasLocation: r.has_location === true,
    lat: n(r.latitude),
    lng: n(r.longitude),
    source: r.location_source === 'camera' || r.location_source === 'picked' ? r.location_source : null,
    locatedByName: str(r.located_by_name),
    optedOut: r.opted_out === true,
    isPoster: r.is_poster === true,
    mayPlace: r.may_place === true,
  }
}

/** Photos the caller cannot see come back absent, never as "no location" (rule 12). */
export async function loadPlaceState(paths: string[]): Promise<Map<string, PlaceState>> {
  const out = new Map<string, PlaceState>()
  if (paths.length === 0) return out
  const { data, error } = await supabase.rpc('photo_place_state', { p_paths: paths.slice(0, 200) })
  if (error) throw error
  for (const r of (data ?? []) as Record<string, unknown>[]) {
    const s = toPlaceState(r)
    out.set(s.path, s)
  }
  return out
}

export async function placePhoto(path: string, lat: number, lng: number): Promise<void> {
  const { error } = await supabase.rpc('place_photo', { p_storage_path: path, p_latitude: lat, p_longitude: lng })
  if (error) throw error
}

/**
 * 324 (FED-76). A place One offers for a photo: a site the company knows
 * (`known`, its own name and point) or one One named. Choosing it is
 * placePhoto - the person who chose it placed the photo.
 */
export type LocationSuggestion = {
  name: string
  kind: 'site' | 'area'
  lat: number
  lng: number
  /** "Antofagasta, Antofagasta Region, Chile" - what is around it. */
  place: string | null
  confidence: number
  why: string
  known: boolean
}

/** photo-room's suggest_location rows as suggestions; anything without a usable point is dropped. */
export function toLocationSuggestions(raw: unknown): LocationSuggestion[] {
  const rows = (raw as { suggestions?: unknown } | null)?.suggestions
  if (!Array.isArray(rows)) return []
  const out: LocationSuggestion[] = []
  for (const r of rows as Record<string, unknown>[]) {
    const lat = Number(r?.latitude)
    const lng = Number(r?.longitude)
    const name = str(r?.name)
    if (!name || r?.latitude == null || r?.longitude == null || !Number.isFinite(lat) || !Number.isFinite(lng)) continue
    if (lat < -90 || lat > 90 || lng < -180 || lng > 180 || (lat === 0 && lng === 0)) continue
    const c = Number(r.confidence)
    out.push({
      name,
      kind: r.kind === 'area' ? 'area' : 'site',
      lat: roundCoord(lat),
      lng: roundCoord(lng),
      place: str(r.place),
      confidence: Number.isFinite(c) ? Math.max(0, Math.min(1, c)) : 0.5,
      why: str(r.why) ?? '',
      known: r.known === true,
    })
  }
  return out
}

/** How sure One is, in three words rather than a percentage. */
export function suggestionSureness(confidence: number): 'likely' | 'possible' | 'guess' {
  return confidence >= 0.7 ? 'likely' : confidence >= 0.4 ? 'possible' : 'guess'
}

/** 324. Ask One where this photo was taken. The server decides who may ask (photo_location_hints). */
export async function suggestLocations(path: string, lang: 'en' | 'es'): Promise<LocationSuggestion[]> {
  const { data, error } = await supabase.functions.invoke('photo-room', { body: { kind: 'suggest_location', path, lang } })
  if (error) throw new Error(await readFunctionError(error))
  return toLocationSuggestions(data)
}

/** Six decimals is about 10 cm: as fine as the column keeps. */
export function roundCoord(v: number): number {
  return Math.round(v * 1e6) / 1e6
}

/** "-23.6509, -70.3975" (or with a semicolon or spaces) as a point, or null.
 *  Rejects out-of-range values and 0,0 — the server's own rule. */
export function parseLatLng(text: string): { lat: number; lng: number } | null {
  const m = /^\s*(-?\d{1,3}(?:\.\d+)?)\s*[,; ]\s*(-?\d{1,3}(?:\.\d+)?)\s*$/.exec(text)
  if (!m) return null
  const lat = Number(m[1])
  const lng = Number(m[2])
  if (lat < -90 || lat > 90 || lng < -180 || lng > 180 || (lat === 0 && lng === 0)) return null
  return { lat: roundCoord(lat), lng: roundCoord(lng) }
}

export function formatLatLng(lat: number, lng: number): string {
  return `${lat.toFixed(5)}, ${lng.toFixed(5)}`
}

export type PlaceHit = { label: string; lat: number; lng: number }

/** MapTiler's geocoder results as hits; anything malformed is dropped. */
export function toPlaceHits(raw: unknown): PlaceHit[] {
  const feats = (raw as { features?: unknown[] } | null)?.features
  if (!Array.isArray(feats)) return []
  const hits: PlaceHit[] = []
  for (const f of feats as { place_name?: unknown; center?: unknown }[]) {
    const c = Array.isArray(f.center) ? f.center : null
    if (typeof f.place_name !== 'string' || !c || !Number.isFinite(Number(c[0])) || !Number.isFinite(Number(c[1]))) continue
    hits.push({ label: f.place_name, lng: Number(c[0]), lat: Number(c[1]) })
  }
  return hits
}

/**
 * Find a place by name — only with a MapTiler key (David's basemap ruling), so
 * the words typed go to the same vendor that serves the map and nowhere else.
 * The typed words, never a person or a photo.
 */
export async function searchPlaces(key: string, query: string, lang: 'en' | 'es'): Promise<PlaceHit[]> {
  const q = query.trim()
  if (!q) return []
  const url = `${MAPTILER_ORIGIN}/geocoding/${encodeURIComponent(q)}.json?key=${encodeURIComponent(key)}&limit=5&language=${lang}`
  const res = await fetch(url)
  if (!res.ok) throw new Error(`place search ${res.status}`)
  return toPlaceHits(await res.json())
}
